import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * Iter88zbn (2026-02-19) — iOS-typische "Swipe-from-Left-Edge"-Geste.
 *
 * Verhalten:
 *  - Startet ein Touch innerhalb der linken 24 px, tracken wir den Finger.
 *  - Ab ≥ 80 px horizontaler Delta (und < 60 px vertikaler Delta) lösen wir
 *    ein `navigate(-1)` aus (Browser-History → Formular-State bleibt).
 *  - Auf Home-Routen (`/dashboard`, `/telesales`, `/login`, `/`) ist die
 *    Geste inaktiv — wie in iOS, wo man auf dem Root-Screen nicht mehr
 *    weiter zurückgehen kann.
 *  - Kaltstart-Schutz: bei `location.key === "default"` (Direktlink) wird
 *    stattdessen `/dashboard` angesteuert.
 *  - Visuelle Rückmeldung: ein kleiner ChevronLeft-Pill wandert vom
 *    linken Rand mit dem Finger mit — genauso wie in nativen iOS-Apps.
 */
const HOME_ROUTES = new Set(["/", "/dashboard", "/telesales", "/login"]);
const EDGE_THRESHOLD_PX = 24;
const TRIGGER_DELTA_X = 80;
const MAX_DELTA_Y = 60;

export default function SwipeBackGesture() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dragX, setDragX] = useState(0);
  // Refs statt State für Werte, die im touchend-Handler nur "letzter
  // gemessener Delta" sein müssen (kein Re-Render nötig, und vermeidet
  // Re-Registrierung der Listener bei jedem Frame).
  const stateRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    lastDx: 0,
  });

  useEffect(() => {
    // Nur auf Touch-Geräten aktiv (Desktop → kein Swipe).
    const hasTouch = typeof window !== "undefined"
      && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (!hasTouch) return;

    const isHome = HOME_ROUTES.has(location.pathname);

    const onStart = (e) => {
      if (isHome) return;
      const t = e.touches?.[0];
      if (!t) return;
      if (t.clientX > EDGE_THRESHOLD_PX) return;
      stateRef.current = {
        active: true,
        startX: t.clientX,
        startY: t.clientY,
        lastDx: 0,
      };
      setDragX(0);
    };

    const onMove = (e) => {
      const st = stateRef.current;
      if (!st.active) return;
      const t = e.touches?.[0];
      if (!t) return;
      const dx = t.clientX - st.startX;
      const dy = Math.abs(t.clientY - st.startY);
      // Verwerfen wenn Nutzer eher vertikal scrollt.
      if (dy > MAX_DELTA_Y) {
        stateRef.current.active = false;
        setDragX(0);
        return;
      }
      const clamped = Math.max(0, Math.min(dx, 220));
      stateRef.current.lastDx = clamped;
      setDragX(clamped);
    };

    const onEnd = (e) => {
      const st = stateRef.current;
      if (!st.active) return;
      const t = e.changedTouches?.[0];
      const dx = t ? (t.clientX - st.startX) : st.lastDx;
      const dy = t ? Math.abs(t.clientY - st.startY) : 0;
      stateRef.current.active = false;
      setDragX(0);
      if (dy > MAX_DELTA_Y) return;
      if (dx < TRIGGER_DELTA_X) return;
      // Trigger — dieselbe Logik wie AppHeader.handleBack.
      // (Iter88zbo: kein Duration-Cap mehr — wenn der Indicator grün ist,
      // muss der Wisch auch triggern, egal wie lange der Nutzer hält.)
      const hasHistory = location.key && location.key !== "default";
      if (hasHistory) {
        navigate(-1);
      } else {
        navigate("/dashboard");
      }
    };

    // `passive: true` — wir wollen native Scroll-Performance behalten und
    // rufen nie `preventDefault` auf. Die visuelle Rückmeldung passiert
    // rein via React-State.
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [location.pathname, location.key, navigate]);

  // Sichtbarer Chevron-Pill, wandert mit dem Finger mit.
  if (dragX <= 6) return null;
  const progress = Math.min(dragX / TRIGGER_DELTA_X, 1);
  const willTrigger = dragX >= TRIGGER_DELTA_X;
  return (
    <div
      data-testid="swipe-back-indicator"
      aria-hidden
      style={{
        position: "fixed",
        top: "50%",
        left: 0,
        transform: `translate(${Math.max(0, dragX - 48)}px, -50%)`,
        transition: "background-color 120ms ease-out",
      }}
      className={`z-[60] pointer-events-none flex items-center justify-center h-14 w-14 rounded-r-2xl shadow-lg ring-1 ring-white/10 ${
        willTrigger
          ? "bg-[#00E575] text-[#09090B]"
          : "bg-zinc-900/85 text-zinc-100"
      }`}
    >
      <ChevronLeft
        size={28}
        strokeWidth={willTrigger ? 3 : 2.4}
        style={{ opacity: 0.5 + 0.5 * progress }}
      />
    </div>
  );
}
